import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { BOOKMAKERS, DEFAULT_BOOKMAKER, MARKET_LABEL, SCAN_MARKETS } from "./valueFormat";

const TYPES = ["image/png", "image/jpeg", "image/webp"];

export function ValueUpload({ day, games = [], comp, selectedId, onResult, contextMarket, contextBook, inputRef }) {
  const input = useRef(null);
  const depth = useRef(0);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [market, setMarket] = useState("");
  const [gameId, setGameId] = useState("");
  const [bookmaker, setBookmaker] = useState(DEFAULT_BOOKMAKER);
  const open = games.filter((g) => g.status === "pre");
  // Follow the game picked in the left column (Auto when it has already started).
  useEffect(() => { setGameId(open.some((g) => g.id === selectedId) ? selectedId : ""); }, [selectedId, games]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (contextMarket !== undefined) setMarket(contextMarket || ""); }, [contextMarket]);
  useEffect(() => { if (contextBook !== undefined) setBookmaker(contextBook || DEFAULT_BOOKMAKER); }, [contextBook]);
  useEffect(() => { if (inputRef) inputRef.current = () => input.current?.click(); }, [inputRef]);
  const send = async (list) => {
    if (busy) return;
    const files = [...list].filter((f) => TYPES.includes(f.type)).slice(0, 6);
    if (!files.length) return toast.error("PNG, JPG or WEBP screenshots only");
    const form = new FormData();
    files.forEach((f, i) => form.append("files", f, f.name || `pasted-${i}.png`));
    form.append("day", day);
    if (market) form.append("market", market);
    if (gameId) form.append("game_id", gameId);
    form.append("bookmaker", bookmaker);
    if (comp) form.append("comp", comp);
    setBusy(true);
    try {
      const { data } = await api.post("/value/scan", form, { timeout: 180000 });
      onResult(data);
      toast.success(`${data.scanned} prices read · ${data.priced ?? 0} saved · ${data.value} value picks`);
      (data.warnings || []).filter((w) => w.includes("already uploaded") || w.includes("same image")).forEach((w) => toast.warning(w));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    const onPaste = (e) => {
      const files = [...(e.clipboardData?.files || [])].filter((f) => TYPES.includes(f.type));
      if (files.length) { e.preventDefault(); sendRef.current(files); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);
  const drag = {
    onDragEnter: (e) => { e.preventDefault(); depth.current += 1; setOver(true); },
    onDragLeave: () => { depth.current -= 1; if (depth.current <= 0) { depth.current = 0; setOver(false); } },
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => { e.preventDefault(); depth.current = 0; setOver(false); send(e.dataTransfer.files); },
  };
  return (
    <div className="vs-upload">
      <div className="vs-upload-opts">
        <label className="vs-select">Market
          <select value={market} onChange={(e) => setMarket(e.target.value)} disabled={busy} data-testid="value-market-select">
            <option value="">Auto (title, or detect from odds)</option>
            {SCAN_MARKETS.map((k) => <option key={k} value={k}>{MARKET_LABEL[k]}</option>)}
          </select>
        </label>
        <label className="vs-select">Game
          <select value={gameId} onChange={(e) => setGameId(e.target.value)} disabled={busy} data-testid="value-game-select">
            <option value="">Auto (all games)</option>
            {open.map((g) => <option key={g.id} value={g.id}>{g.away_code} @ {g.home_code}</option>)}
          </select>
        </label>
        <label className="vs-select">Bookmaker
          <select value={bookmaker} onChange={(e) => setBookmaker(e.target.value)} disabled={busy} data-testid="value-bookmaker-select">
            {BOOKMAKERS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
      </div>
      <div role="button" tabIndex={0} className={`vs-drop ${over ? "is-over" : ""} ${busy ? "is-busy" : ""}`} {...drag}
        onClick={() => !busy && input.current?.click()} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !busy && input.current?.click()}
        data-testid="value-dropzone">
        <input ref={input} type="file" accept={TYPES.join(",")} multiple hidden onChange={(e) => send(e.target.files)} data-testid="value-file-input" />
        {busy ? <Loader2 size={26} className="vs-spin" /> : <ImagePlus size={26} />}
        <b data-testid="value-upload-btn">{busy ? "Reading odds…" : over ? "Drop to scan" : "Drag & drop odds screenshots here"}</b>
        <span>or click to choose · or paste with Ctrl+V · up to 6 images · PTS, REB, AST, 3PM, PRA</span>
      </div>
    </div>
  );
}
